import { useEffect, useMemo, useState } from 'react'
import { Volume2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/components/ui/cn'
import { copy } from '@/lib/copy'
import { expandAccepted, isAccepted, matchAnswer, normalizeAnswer } from '@/lib/answerMatch'
import { playPronunciation } from '@/lib/playPronunciation'
import { SenseGlossLine } from '@/components/vocabulary/SenseGlossLine'
import type { ReviewCard } from '@/api/review'

interface FlashCardProps {
  card: ReviewCard
  isFlipped: boolean
  onFlip: () => void
  userInput?: string
  onInputChange?: (value: string) => void
  selectedOptionId?: string | null
  onSelectOption?: (optionId: string) => void
  fillBlankRevealed?: boolean
  onCheckFillBlank?: () => void
  onCheckFillBlankBlocked?: () => void
  /** Skip flip animation when advancing to the next card */
  disableFlipTransition?: boolean
}

function isCorrectOption(optionText: string, answerText: string) {
  return normalizeAnswer(optionText) === normalizeAnswer(answerText)
}

function PlayAudioButton({
  text,
  url,
  className,
}: {
  text: string
  url?: string
  className?: string
}) {
  if (!text.trim() && !url) return null

  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation()
        playPronunciation(text, url)
      }}
      className={cn(
        'rounded p-1 text-muted-foreground transition-colors hover:text-foreground',
        className,
      )}
      aria-label="Play pronunciation"
      data-testid="review-play-audio"
    >
      <Volume2 className="h-5 w-5" />
    </button>
  )
}

function ReviewHint({
  hint,
  className,
}: {
  hint?: string
  className?: string
}) {
  return <SenseGlossLine gloss={hint} className={className} />
}

function PromptHeading({
  text,
  audioUrl,
  phonetic,
  large,
  compact,
}: {
  text: string
  audioUrl?: string
  phonetic?: string
  large?: boolean
  compact?: boolean
}) {
  return (
    <div className="flex flex-wrap items-baseline justify-center gap-x-2 gap-y-0.5">
      <h2
        className={cn(
          'font-bold text-foreground',
          large ? 'text-4xl' : compact ? 'text-xl sm:text-2xl md:text-3xl' : 'text-3xl',
        )}
      >
        {text}
      </h2>
      {phonetic && (
        <span className="text-base font-normal text-muted-foreground">{phonetic}</span>
      )}
      <PlayAudioButton text={text} url={audioUrl} className="shrink-0 self-center" />
    </div>
  )
}

export function FlashCard({
  card,
  isFlipped,
  onFlip,
  userInput,
  onInputChange,
  selectedOptionId = null,
  onSelectOption,
  fillBlankRevealed = false,
  onCheckFillBlank,
  onCheckFillBlankBlocked,
  disableFlipTransition = false,
}: FlashCardProps) {
  const [hintVisible, setHintVisible] = useState(false)

  useEffect(() => {
    setHintVisible(false)
  }, [card.id])
  const isFlipMode = card.mode === 'CLASSIC' || card.mode === 'REVERSE'
  const isMultipleChoice = card.mode === 'MULTIPLE_CHOICE'
  const isCompactInteractive = isMultipleChoice || card.mode === 'FILL_BLANK'
  const fillBlankAccepted = useMemo(
    () => expandAccepted(card.answerText, card.knownTranslations),
    [card.answerText, card.knownTranslations],
  )
  const fillBlankMatch = fillBlankRevealed
    ? matchAnswer(userInput ?? '', fillBlankAccepted)
    : 'miss'
  const fillBlankCorrect = fillBlankRevealed && isAccepted(fillBlankMatch)
  const showEnglishAudioOnPrompt = card.mode === 'CLASSIC' || card.mode === 'MULTIPLE_CHOICE'
  const mcRevealed = isMultipleChoice && selectedOptionId != null
  const mcOptions = card.options ?? []
  /** MWU shortExplanation must not spoil Word→translation / Reverse prompt. */
  const isPhraseUnit = Boolean(card.unitType && card.unitType !== 'WORD')
  /** C4 — grammar Episode: meet-again framing, not generic Prompt/Answer */
  const isGrammarEpisode = card.unitType === 'GRAMMAR'
  const promptLabel = isGrammarEpisode ? copy.review.meetAgain : 'Prompt'
  const answerLabel = isGrammarEpisode ? copy.review.momentMeaning : 'Answer'
  const promptSenseGloss = isPhraseUnit ? undefined : card.senseGloss
  const answerSenseGloss = isPhraseUnit ? card.senseGloss : undefined
  const momentSentence =
    isGrammarEpisode && card.examples.length > 0 ? card.examples[0] : undefined

  if (isFlipMode) {
    return (
      <div className="mx-auto w-full max-w-3xl" style={{ perspective: '1200px' }}>
        <div
          className="relative h-80 w-full cursor-pointer"
          onClick={onFlip}
          style={{
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            transition: disableFlipTransition ? 'transform 0ms' : 'transform 500ms ease',
          }}
        >
          <Card
            className="absolute inset-0 flex items-center justify-center p-8 text-center"
            style={{ backfaceVisibility: 'hidden' }}
            data-testid={isGrammarEpisode ? 'review-grammar-episode-front' : undefined}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                {promptLabel}
              </p>
              {card.mode === 'CLASSIC' ? (
                <div className="mt-4">
                  <PromptHeading text={card.questionText} audioUrl={card.audioUrl} large />
                  {card.phonetic ? <p className="mt-2 text-sm text-muted-foreground">{card.phonetic}</p> : null}
                </div>
              ) : (
                <h2 className="mt-4 text-4xl font-bold text-foreground">{card.questionText}</h2>
              )}
              {momentSentence ? (
                <p
                  className="mt-3 line-clamp-2 text-sm italic leading-relaxed text-muted-foreground"
                  data-testid="review-grammar-episode-sentence"
                >
                  &ldquo;{momentSentence}&rdquo;
                </p>
              ) : null}
              <ReviewHint hint={promptSenseGloss} />
              {card.partOfSpeech ? <p className="mt-3 text-sm italic text-muted-foreground">{card.partOfSpeech}</p> : null}
            </div>
          </Card>

          <Card
            className="absolute inset-0 flex items-center justify-center p-8 text-center"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
            data-testid={isGrammarEpisode ? 'review-grammar-episode-back' : undefined}
          >
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {answerLabel}
                </p>
                {card.mode === 'REVERSE' ? (
                  <div className="mt-4">
                    <PromptHeading text={card.answerText} audioUrl={card.audioUrl} large />
                    {card.phonetic ? <p className="mt-2 text-sm text-muted-foreground">{card.phonetic}</p> : null}
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    {(card.knownTranslations && card.knownTranslations.length > 1
                      ? card.knownTranslations
                      : card.answerText.split('\n')
                    ).map((translation) => (
                      <p key={translation} className="text-2xl font-bold text-foreground">
                        {translation}
                      </p>
                    ))}
                  </div>
                )}
                <ReviewHint hint={answerSenseGloss} />
              </div>
              {!isGrammarEpisode && card.examples.length > 0 ? (
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {card.examples.slice(0, 2).map((example) => (
                    <li key={example}>&ldquo;{example}&rdquo;</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <Card
      className={cn(
        'mx-auto w-full max-w-3xl',
        isCompactInteractive ? 'p-3 sm:p-6 md:p-8' : 'p-8',
      )}
    >
      <div className={cn(isCompactInteractive ? 'space-y-3 md:space-y-6' : 'space-y-6')}>
        <div>
          <p
            className={cn(
              'text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground',
              isCompactInteractive && 'max-md:sr-only',
            )}
          >
            {promptLabel}
          </p>
          {showEnglishAudioOnPrompt ? (
            <div className={cn(isMultipleChoice ? 'mt-0 md:mt-3' : 'mt-3')}>
              <PromptHeading
                text={card.questionText}
                audioUrl={card.audioUrl}
                phonetic={card.phonetic}
                compact={isMultipleChoice}
              />
            </div>
          ) : (
            <h2
              className={cn(
                'mt-3 font-bold text-foreground',
                card.mode === 'FILL_BLANK' && card.questionText.includes('___')
                  ? 'text-xl leading-relaxed sm:text-2xl'
                  : 'text-3xl',
              )}
            >
              {card.questionText}
            </h2>
          )}
          <ReviewHint
            hint={promptSenseGloss}
            className={isMultipleChoice ? 'mt-1 line-clamp-2 text-xs md:mt-2 md:text-sm md:line-clamp-none' : undefined}
          />
          {card.mode === 'FILL_BLANK' ? (
            <div className="mt-3 min-h-[22px]">
              {!fillBlankRevealed && (
                hintVisible ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground/80">{card.answerText}</span>
                    {card.phonetic && <span>{card.phonetic}</span>}
                    <PlayAudioButton text={card.answerText} url={card.audioUrl} />
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setHintVisible(true)}
                    className="text-xs text-muted-foreground/50 transition-colors underline-offset-2 hover:text-muted-foreground hover:underline"
                  >
                    Show hint
                  </button>
                )
              )}
              {fillBlankRevealed && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground/80">{card.answerText}</span>
                  {card.phonetic && <span>{card.phonetic}</span>}
                  <PlayAudioButton text={card.answerText} url={card.audioUrl} />
                </div>
              )}
            </div>
          ) : null}
        </div>

        {card.mode === 'FILL_BLANK' ? (
          <div className="space-y-3" data-testid="fill-blank-input">
            <Input
              placeholder="Type the missing word"
              value={userInput ?? ''}
              disabled={fillBlankRevealed}
              onChange={(event) => onInputChange?.(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  onCheckFillBlank?.()
                }
              }}
              className={cn(
                fillBlankRevealed && fillBlankCorrect && 'border-green-600 focus-visible:ring-green-600/30',
                fillBlankRevealed && !fillBlankCorrect && 'border-destructive focus-visible:ring-destructive/30',
              )}
            />
            {fillBlankRevealed && (
              <p
                className={cn(
                  'text-sm font-medium',
                  fillBlankCorrect ? 'text-green-700 dark:text-green-400' : 'text-destructive',
                )}
                data-testid="fill-blank-feedback"
              >
                {fillBlankMatch === 'exact'
                  ? copy.review.fillBlankCorrect
                  : fillBlankMatch === 'soft'
                    ? `${copy.review.fillBlankSoftCorrect} ${card.answerText}`
                    : `${copy.review.fillBlankWrong} ${card.answerText}`}
              </p>
            )}
            {!fillBlankRevealed ? (
              <Button
                className={cn(
                  'min-h-12 w-full',
                  !userInput?.trim() && 'pointer-events-auto opacity-50 saturate-[0.85]',
                )}
                aria-disabled={!userInput?.trim()}
                onClick={() => {
                  if (!userInput?.trim()) {
                    onCheckFillBlankBlocked?.()
                    return
                  }
                  onCheckFillBlank?.()
                }}
                data-testid="fill-blank-check"
              >
                {copy.review.checkAnswer}
              </Button>
            ) : null}
          </div>
        ) : null}

        {isMultipleChoice ? (
          <div className="grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-3" data-testid="mc-options">
            {mcOptions.map((option) => {
              const revealed = mcRevealed
              const isSelected = selectedOptionId === option.id
              const isCorrect = isCorrectOption(option.text, card.answerText)

              return (
                <button
                  key={option.id}
                  type="button"
                  disabled={revealed}
                  onClick={() => onSelectOption?.(option.id)}
                  data-testid={`mc-option-${option.id}`}
                  className={cn(
                    'rounded-lg border px-2 py-2 text-left text-xs font-medium transition-colors sm:px-3 sm:py-2.5 sm:text-sm',
                    !revealed && 'border-border hover:bg-accent hover:text-accent-foreground',
                    revealed && isCorrect && 'border-green-600 bg-green-500/10 text-foreground dark:border-green-500 dark:bg-green-900/25',
                    revealed && isSelected && !isCorrect && 'border-destructive bg-destructive/10 text-foreground',
                    revealed && !isSelected && !isCorrect && 'border-border text-muted-foreground opacity-60',
                  )}
                >
                  <span className="mr-1 text-muted-foreground sm:mr-2">{option.id}.</span>
                  <span className="break-words">{option.text}</span>
                  {revealed && isCorrect && (
                    <span className="mt-0.5 block text-[10px] font-semibold text-green-700 sm:mt-0 sm:ml-2 sm:inline sm:text-xs dark:text-green-400">
                      Correct
                    </span>
                  )}
                  {revealed && isSelected && !isCorrect && (
                    <span className="mt-0.5 block text-[10px] font-semibold text-destructive sm:mt-0 sm:ml-2 sm:inline sm:text-xs">
                      Wrong
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        ) : null}

        {card.examples.length > 0 && card.mode !== 'FILL_BLANK' && !isMultipleChoice ? (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Examples</p>
            <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
              {card.examples.slice(0, 2).map((example) => (
                <li key={example}>&ldquo;{example}&rdquo;</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  )
}
